import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import CookieConsent from './components/common/CookieConsent';
import ScrollToTop from './components/common/ScrollToTop';

// Lazy loading pour les pages publiques
const HomePage = lazy(() => import('./pages/HomePage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'));
const EtudesDeCasPage = lazy(() => import('./pages/EtudesDeCasPage'));
const RessourcesPage = lazy(() => import('./pages/RessourcesPage'));
const ChecklistAuditPage = lazy(() => import('./pages/ChecklistAuditPage'));
const RoadmapTemplatePage = lazy(() => import('./pages/RoadmapTemplatePage'));

// Pages SEO articles
const DeployerAgentIaPmeSuissePage = lazy(() => import('./pages/DeployerAgentIaPmeSuissePage'));
const CoutAutomatisationPmeSuissePage = lazy(() => import('./pages/CoutAutomatisationPmeSuissePage'));
const MakeVsN8nVsZapierPage = lazy(() => import('./pages/MakeVsN8nVsZapierPage'));
const LpdIaPmeSuissePage = lazy(() => import('./pages/LpdIaPmeSuissePage'));
const AutomatisationFiduciaireSuissePage = lazy(() => import('./pages/AutomatisationFiduciaireSuissePage'));
const ProcessusAutomatiserPmeSuissePage = lazy(() => import('./pages/ProcessusAutomatiserPmeSuissePage'));
const AuditProcessusPmeSuissePage = lazy(() => import('./pages/AuditProcessusPmeSuissePage'));
const MeilleursSaasPmeSuisse2026Page = lazy(() => import('./pages/MeilleursSaasPmeSuisse2026Page'));
const RoiIaPmeSuissePage = lazy(() => import('./pages/RoiIaPmeSuissePage'));
const TransformationDigitalePmeSuissePage = lazy(() => import('./pages/TransformationDigitalePmeSuissePage'));
const ChatbotVsAgentIaPmePage = lazy(() => import('./pages/ChatbotVsAgentIaPmePage'));
const ChoisirConsultantIaPmeSuissePage = lazy(() => import('./pages/ChoisirConsultantIaPmeSuissePage'));

// Pages landing villes
const AgentIaLausannePage = lazy(() => import('./pages/AgentIaLausannePage'));
const AgentIaGenevePage = lazy(() => import('./pages/AgentIaGenevePage'));
const AgentIaFribourgPage = lazy(() => import('./pages/AgentIaFribourgPage'));

// Landing pages long-tail P1
const GagnerDuTempsAdministratifPmePage = lazy(() => import('./pages/GagnerDuTempsAdministratifPmePage'));
const RemplacerSecretaireTempsPartielPage = lazy(() => import('./pages/RemplacerSecretaireTempsPartielPage'));
const RelancesClientsAutomatiquesSuissePage = lazy(() => import('./pages/RelancesClientsAutomatiquesSuissePage'));
const DigitalisationPmeVaudPage = lazy(() => import('./pages/DigitalisationPmeVaudPage'));
const AnalyseComptableSansFiduciairePage = lazy(() => import('./pages/AnalyseComptableSansFiduciairePage'));
const TrouverClientsSansProspectionManuellelPage = lazy(() => import('./pages/TrouverClientsSansProspectionManuellelPage'));

// Pages villes P2
const AutomatisationPmeLausannePage = lazy(() => import('./pages/AutomatisationPmeLausannePage'));
const DigitalisationEntrepriseVaudPage = lazy(() => import('./pages/DigitalisationEntrepriseVaudPage'));
const GagnerDuTempsPmeNyonPage = lazy(() => import('./pages/GagnerDuTempsPmeNyonPage'));
const GagnerDuTempsPmeMorgesPage = lazy(() => import('./pages/GagnerDuTempsPmeMorgesPage'));

// Articles blog P2
const CombienDeTempsPmeAdministratifPage = lazy(() => import('./pages/articles/CombienDeTempsPmeAdministratifPage'));
const AutomatiserRelancesClientsSansChangerLogicielPage = lazy(() => import('./pages/articles/AutomatiserRelancesClientsSansChangerLogicielPage'));
const FiduciaireVsOutilAutomatiseComptaPmePage = lazy(() => import('./pages/articles/FiduciaireVsOutilAutomatiseComptaPmePage'));
const SignesDigitaliserAdministratifPmePage = lazy(() => import('./pages/articles/SignesDigitaliserAdministratifPmePage'));
const ExternaliserOuAutomatiserPmePage = lazy(() => import('./pages/articles/ExternaliserOuAutomatiserPmePage'));

import './App.css';
import './styles/purple-theme.css';

const AppContent = () => {
  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen pt-20 lg:pt-24">
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        }>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/mentions-legales" element={<LegalPage />} />
            <Route path="/confidentialite" element={<PrivacyPage />} />
            <Route path="/conditions" element={<TermsPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/etudes-de-cas" element={<EtudesDeCasPage />} />
            <Route path="/ressources" element={<RessourcesPage />} />
            <Route path="/checklist-audit-processus" element={<ChecklistAuditPage />} />
            <Route path="/template-roadmap-transformation" element={<RoadmapTemplatePage />} />
            <Route path="/deployer-agent-ia-pme-suisse" element={<DeployerAgentIaPmeSuissePage />} />
            <Route path="/cout-automatisation-pme-suisse" element={<CoutAutomatisationPmeSuissePage />} />
            <Route path="/make-vs-n8n-vs-zapier-pme-suisse" element={<MakeVsN8nVsZapierPage />} />
            <Route path="/lpd-ia-pme-suisse" element={<LpdIaPmeSuissePage />} />
            <Route path="/automatisation-fiduciaire-suisse" element={<AutomatisationFiduciaireSuissePage />} />
            <Route path="/processus-automatiser-pme-suisse" element={<ProcessusAutomatiserPmeSuissePage />} />
            <Route path="/audit-processus-pme-suisse" element={<AuditProcessusPmeSuissePage />} />
            <Route path="/meilleurs-saas-pme-suisse-2026" element={<MeilleursSaasPmeSuisse2026Page />} />
            <Route path="/roi-ia-pme-suisse" element={<RoiIaPmeSuissePage />} />
            <Route path="/transformation-digitale-pme-suisse" element={<TransformationDigitalePmeSuissePage />} />
            <Route path="/chatbot-vs-agent-ia-pme" element={<ChatbotVsAgentIaPmePage />} />
            <Route path="/choisir-consultant-ia-pme-suisse" element={<ChoisirConsultantIaPmeSuissePage />} />

            {/* Landing pages long-tail P1 */}
            <Route path="/gagner-du-temps-administratif-pme" element={<GagnerDuTempsAdministratifPmePage />} />
            <Route path="/remplacer-secretaire-temps-partiel" element={<RemplacerSecretaireTempsPartielPage />} />
            <Route path="/relances-clients-automatiques-suisse" element={<RelancesClientsAutomatiquesSuissePage />} />
            <Route path="/digitalisation-pme-vaud" element={<DigitalisationPmeVaudPage />} />
            <Route path="/analyse-comptable-sans-fiduciaire" element={<AnalyseComptableSansFiduciairePage />} />
            <Route path="/trouver-clients-sans-prospection-manuelle" element={<TrouverClientsSansProspectionManuellelPage />} />

            {/* Pages landing villes */}
            <Route path="/agent-ia-lausanne" element={<AgentIaLausannePage />} />
            <Route path="/agent-ia-geneve" element={<AgentIaGenevePage />} />
            <Route path="/agent-ia-fribourg" element={<AgentIaFribourgPage />} />
            <Route path="/automatisation-pme-lausanne" element={<AutomatisationPmeLausannePage />} />
            <Route path="/digitalisation-entreprise-vaud" element={<DigitalisationEntrepriseVaudPage />} />
            <Route path="/gagner-du-temps-pme-nyon" element={<GagnerDuTempsPmeNyonPage />} />
            <Route path="/gagner-du-temps-pme-morges" element={<GagnerDuTempsPmeMorgesPage />} />

            {/* Articles blog P2 */}
            <Route path="/combien-de-temps-pme-taches-administratives" element={<CombienDeTempsPmeAdministratifPage />} />
            <Route path="/automatiser-relances-clients-sans-changer-logiciel" element={<AutomatiserRelancesClientsSansChangerLogicielPage />} />
            <Route path="/fiduciaire-vs-outil-automatise-compta-pme" element={<FiduciaireVsOutilAutomatiseComptaPmePage />} />
            <Route path="/signes-digitaliser-administratif-pme" element={<SignesDigitaliserAdministratifPmePage />} />
            <Route path="/externaliser-ou-automatiser-pme" element={<ExternaliserOuAutomatiserPmePage />} />

            {/* Redirects 301 — anciennes URLs (avant WordPress / refonte) */}
            <Route path="/contactez-nous" element={<Navigate to="/contact" replace />} />
            <Route path="/a-propos-de-nous" element={<Navigate to="/about" replace />} />
            <Route path="/il-nous-font-confiance" element={<Navigate to="/partners" replace />} />
            <Route path="/workflow-n8n-entreprise" element={<Navigate to="/make-vs-n8n-vs-zapier-pme-suisse" replace />} />
            {/* WordPress archive slugs → 404 propre */}
            <Route path="/category/*" element={<NotFoundPage />} />
            <Route path="/author/*" element={<NotFoundPage />} />
            <Route path="/2025/*" element={<NotFoundPage />} />
            <Route path="/2026/*" element={<NotFoundPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieConsent />
    </>
  );
};

function App() {
  return (
    <div className="App purple-gradient-bg">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
